/*
 * Decléor — slideshow video behaviours: play-on-screen, hold-until-end, and the blurred wash
 * ---------------------------------------------------------------------------
 * Three features, one file because they belong to the same slide and the section emits one
 * script tag for any of them:
 *   1. PLAY   — only the video actually on screen downloads and plays   (data-ss-autoplay)
 *   2. HOLD   — a video slide stays until its video ends                (data-ss-video-end)
 *   3. REVEAL — the video fades in once it is really playing            (data-ss-fade)
 *   4. WASH   — a blurred backdrop for a contained video                (data-ss-video-blur)
 * Each no-ops unless its own marker is present. PLAY, REVEAL and the wash are documented at
 * setupPlay(), setupReveal() and setupFit(), further down.
 *
 * The Slideshow section autoplays on one interval for every slide ("Time between slides",
 * 8s on this store). That is right for an image and wrong for a video: the home page's
 * video slide is 26.7s long, so it was cut off at 8s and the visitor never saw two thirds
 * of it. This script makes a video slide hold until its video ENDS, and leaves image
 * slides on the merchant's interval exactly as before.
 *
 * WHY IT DRIVES FLICKITY THROUGH SYNTHETIC HOVER, WHICH LOOKS ODD UNTIL YOU TRY THE
 * ALTERNATIVES.
 * theme.js builds the slideshow as `new FlickityFade(wrapper, {autoPlay: speed})` and keeps
 * the instance on its own section object (theme.js:5565). Nothing exposes it:
 * `window.Flickity` is `undefined` (measured — the bundle is module-scoped), so
 * `Flickity.data(el)`, `stopPlayer()` and `playPlayer()` are all out of reach, and
 * assets/theme.js is a vendored bundle we do not edit.
 *
 * What IS reachable is Flickity's own hover behaviour. `pauseAutoPlayOnHover` defaults to
 * true and theme.js does not turn it off, so `onmouseenter` pauses the player and
 * `onmouseleave` unpauses it (vendor.js:4427-4437). Dispatching those two events on the
 * wrapper therefore pauses and resumes the real player, with no instance and no patching.
 * Verified end to end before this file was written: free-running the slideshow advanced
 * 0 → 2 in 10s; after a synthetic `mouseenter` it stayed on the same slide for 12s; after
 * `mouseleave` it advanced again.
 *
 * Advancing uses the same route: `[data-slide-custom-next]` already has an onclick bound to
 * `flkty.next()` (theme.js:5573), so a click on it is a real instance call.
 *
 * The two rejected approaches, for whoever reads this next:
 *   - Turning the section's autoplay off in Liquid and scheduling every slide ourselves.
 *     Deterministic, but it makes this file load-bearing: if it ever fails to load, the
 *     slideshow stops advancing at all. With the hover lever, a failure to load leaves the
 *     stock behaviour intact.
 *   - Re-selecting the slide to "extend" it. Fights the player instead of pausing it, and
 *     restarts the fade on every tick.
 *
 * THE SLIDE IS TRACKED BY CLASS, NOT BY EVENT. Flickity's `select` event is only available
 * on the instance, so selection is read from the `is-selected` class Flickity writes on the
 * cells, watched with one MutationObserver per slideshow.
 *
 * IT MUST NEVER STRAND THE SLIDESHOW. A held slide whose video never ends would freeze the
 * carousel for good, so every hold carries a deadline: the video's own duration plus a
 * margin once metadata has landed, and the merchant's interval plus a margin before that.
 * A play() rejection (autoplay blocked — reported by PLAY as `dc:ss:playfail`, since PLAY is
 * the only caller of play()), an `error`, or a `stalled` video releases the hold immediately.
 * In every failure path the slideshow falls back to the interval it has today.
 *
 * Only slides carrying `data-ss-video-end` participate in the hold — the section emits that
 * when "Hold video until it ends" is on. No-ops on any page without such a slide.
 */
(function () {
  'use strict';

  var SEL = {
    wrapper: '[data-slideshow-wrapper]',
    slide: '[data-slideshow-index]',
    next: '[data-slide-custom-next]',
  };
  var MARK = 'data-ss-video-end';
  var SELECTED = 'is-selected';
  /* Fired on a <video> by PLAY when play() is refused for good, for HOLD to release the slide. */
  var PLAYFAIL = 'dc:ss:playfail';
  /* Margin added to a video's own duration before the deadline fires. Generous on purpose:
     it is a safety net, not a schedule, and cutting a video short is the bug being fixed. */
  var GRACE = 2000;

  function setup(wrapper) {
    if (wrapper.__dcSsVideo) {
      return;
    }
    /* Only meaningful while the section actually autoplays: with autoplay off nothing
       advances on its own, so there is nothing to hold back. */
    if (wrapper.getAttribute('data-slideshow-autoplay') !== 'true') {
      return;
    }
    var slides = wrapper.querySelectorAll(SEL.slide);
    var marked = wrapper.querySelectorAll('[' + MARK + ']');
    /* `> 1` guards the one case where holding cannot work: a single-slide carousel. advance() clicks
       [data-slide-custom-next], Flickity has no second cell, and the hold would never be released —
       the failure the header promises never to cause. sections/section-slideshow.liquid already stops
       emitting the marker below two slides, so this is the belt to that braces: it also covers a page
       served from cache with the old markup. */
    if (!(slides.length > 1 && marked.length)) {
      return;
    }
    wrapper.__dcSsVideo = true;

    var speed = parseInt(wrapper.getAttribute('data-slideshow-speed'), 10) || 8000;
    var held = false; // only ever release a hold WE placed, so a real cursor is left alone
    var deadline = 0;
    var current = null; // the video we are currently waiting on
    var onEnded = null;
    var onMeta = null;
    var onFail = null;

    function hold() {
      if (!held) {
        held = true;
        wrapper.dispatchEvent(new Event('mouseenter'));
      }
    }

    function release() {
      if (held) {
        held = false;
        wrapper.dispatchEvent(new Event('mouseleave'));
      }
    }

    function detach() {
      if (deadline) {
        window.clearTimeout(deadline);
        deadline = 0;
      }
      if (current) {
        if (onEnded) {
          current.removeEventListener('ended', onEnded);
        }
        if (onMeta) {
          current.removeEventListener('loadedmetadata', onMeta);
          current.removeEventListener('durationchange', onMeta);
        }
        if (onFail) {
          current.removeEventListener('error', onFail);
          current.removeEventListener('stalled', onFail);
          current.removeEventListener(PLAYFAIL, onFail);
        }
      }
      onEnded = null;
      onMeta = null;
      onFail = null;
      current = null;
    }

    /* Advance, then release. Flickity restarts the player's tick on select, so unpausing
       afterwards leaves the NEXT slide a full interval rather than the remainder of this
       one. Releasing first would hand it whatever was left when we paused. */
    function advance() {
      var next = wrapper.querySelector(SEL.next);
      detach();
      if (next) {
        next.click();
      }
      window.requestAnimationFrame(release);
    }

    function arm(video) {
      var ms = speed + GRACE;
      if (video.duration > 0 && video.duration !== Infinity) {
        /* The whole duration, not what is left of it: PLAY rewinds the video when its slide is
           selected, and nothing guarantees it does so before this runs. Counting from a stale
           currentTime would fire the deadline before the video ends, which is the very bug the
           hold exists to fix. Too late a deadline only costs slack in a safety net; `ended`
           is what normally advances. */
        ms = video.duration * 1000 + GRACE;
      }
      if (deadline) {
        window.clearTimeout(deadline);
      }
      deadline = window.setTimeout(advance, ms);
    }

    function watch(slide) {
      detach();

      var video = slide.hasAttribute(MARK) ? slide.querySelector('video') : null;
      if (!video) {
        /* An image slide, or a video slide the merchant left out of this behaviour: the
           section's own interval governs, which is the whole point of the setting. */
        release();
        return;
      }

      hold();
      current = video;

      onEnded = advance;
      onFail = advance;
      onMeta = function () {
        arm(video);
      };
      video.addEventListener('ended', onEnded);
      video.addEventListener('error', onFail);
      video.addEventListener('stalled', onFail);
      /* Autoplay blocked, or no decodable source. Do not strand the carousel on a frame that
         will never move. */
      video.addEventListener(PLAYFAIL, onFail);
      video.addEventListener('loadedmetadata', onMeta);
      video.addEventListener('durationchange', onMeta);

      /* Rewinding and playing are PLAY's job (setupPlay below), which also knows whether the
         slideshow is on screen at all: a hold must not start a download in the twin section
         hidden for this device. `preload: 'none'` means the first pass often has no metadata
         yet, hence the re-arm above. */
      arm(video);
    }

    function selected() {
      for (var i = 0; i < slides.length; i += 1) {
        if (slides[i].classList.contains(SELECTED)) {
          return slides[i];
        }
      }
      return null;
    }

    var scheduled = 0;
    function sync() {
      scheduled = 0;
      var slide = selected();
      if (slide && slide !== sync.last) {
        sync.last = slide;
        watch(slide);
      }
    }
    sync.last = null;

    /* Flickity toggles `is-selected` on two cells per transition (the leaving one and the
       arriving one), so the callback is coalesced into a single frame. */
    function schedule() {
      if (!scheduled) {
        scheduled = window.requestAnimationFrame(sync);
      }
    }

    if (typeof MutationObserver === 'function') {
      var mo = new MutationObserver(schedule);
      for (var i = 0; i < slides.length; i += 1) {
        mo.observe(slides[i], { attributes: true, attributeFilter: ['class'] });
      }
    }
    sync();
  }

  /* ---- PLAY: only the video on screen downloads and plays --------------------------------
   * Measured on the 2.3.0 preview before this existed: every video slide carried the autoplay
   * attribute, so a phone downloaded all six videos of the home page (40 MB in 15s) and played
   * them at once — the three of the other language slides stacked behind Flickity's fade, and the
   * three desktop ones inside a section that is `display: none` on a phone. CSS hides an element;
   * it never stops its download.
   *
   * The markup now does the part that cannot wait for a script: the FIRST slide's video keeps
   * its autoplay attribute (it is on screen at load), and every <source> carries a `media` that
   * repeats the section's device breakpoints, so the hidden twin requests nothing. Everything else
   * is driven from here:
   *   - the selected slide's video plays, from its first frame when the slide has just been
   *     selected; every other video of the slideshow is paused;
   *   - nothing plays while the slideshow is off screen or hidden. An IntersectionObserver
   *     tracks that, and it never reports a `display: none` element as visible;
   *   - a video whose sources did not match at load (the window has since been resized across
   *     the breakpoint) is reloaded, which makes the browser run source selection again.
   * Selection is read from Flickity's `is-selected` class, as in HOLD and for the same reason.
   *
   * THE NEXT VIDEO IS NOT WARMED UP IN ADVANCE, and that was measured, not assumed. Warming it
   * with preload="metadata" made it start 21ms after its slide was selected instead of 84ms, a
   * gain hidden by the fade anyway. But the warmed video paints its first frame behind the slide
   * on screen, and Chrome takes that late paint as the page's LCP: 0.94s on a phone and 1.62s on
   * desktop, against 0.44s and 0.42s without it.
   *
   * Without this file only the first slide's video plays. That is the one case where a missing
   * script costs something, and the slideshow cannot change slides without theme.js anyway.
   */
  function setupPlay(wrapper) {
    if (wrapper.__dcSsPlay) {
      return;
    }
    if (!wrapper.querySelector('[data-ss-autoplay] video')) {
      return;
    }
    wrapper.__dcSsPlay = true;

    var slides = wrapper.querySelectorAll(SEL.slide);
    var current = null;
    var shown = onScreen();

    function onScreen() {
      var r = wrapper.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < vh;
    }

    function selected() {
      var i;
      for (i = 0; i < slides.length; i += 1) {
        if (slides[i].classList.contains(SELECTED)) {
          return slides[i];
        }
      }
      /* Before Flickity has run, the first cell is the one on screen. */
      return slides[0] || null;
    }

    function autoVideo(slide) {
      return slide ? slide.querySelector('[data-ss-autoplay] video') : null;
    }

    function play(video) {
      /* NETWORK_NO_SOURCE: no <source> matched its `media` when the page loaded. load() runs
         source selection again against the current viewport. */
      if (video.networkState === 3) {
        video.load();
      }
      var playing = video.play();
      if (playing && typeof playing.catch === 'function') {
        playing.catch(function (err) {
          /* AbortError means a pause() or load() overtook this play(): the slide changed or
             scrolled away, so we changed our mind. Anything else is a real refusal. */
          if (err && err.name === 'AbortError') {
            return;
          }
          video.dispatchEvent(new Event(PLAYFAIL));
        });
      }
    }

    function settle(video, target, rewind) {
      if (video !== target) {
        /* Every other video, autoplaying or not: a visitor who started one with its controls
           does not want to hear it from a slide that has gone. */
        if (!video.paused) {
          video.pause();
        }
        return;
      }
      /* Seeking a media element with no data can throw InvalidStateError, and with nothing
         loaded it starts at 0 anyway — hence the guard rather than a try/catch. */
      if (rewind && video.readyState > 0) {
        video.currentTime = 0;
      }
      if (video.paused) {
        play(video);
      }
    }

    function apply(rewind) {
      var target = shown ? autoVideo(current) : null;
      var videos = wrapper.querySelectorAll(SEL.slide + ' video');
      var i;
      for (i = 0; i < videos.length; i += 1) {
        settle(videos[i], target, rewind);
      }
    }

    var scheduled = 0;
    function onSelect() {
      scheduled = 0;
      var slide = selected();
      if (slide === current) {
        return;
      }
      /* No rewind on the first pass: the first slide's video has been playing since the markup
         was parsed, and jumping it back to 0 under the visitor's eyes is the one thing to avoid. */
      var rewind = current !== null;
      current = slide;
      apply(rewind);
    }

    function watchSelection() {
      if (typeof MutationObserver !== 'function') {
        return;
      }
      var mo = new MutationObserver(function () {
        if (!scheduled) {
          scheduled = window.requestAnimationFrame(onSelect);
        }
      });
      var i;
      for (i = 0; i < slides.length; i += 1) {
        mo.observe(slides[i], { attributes: true, attributeFilter: ['class'] });
      }
    }

    function watchVisibility() {
      if (typeof IntersectionObserver !== 'function') {
        return;
      }
      new IntersectionObserver(function (entries) {
        /* Several entries can be queued for the one target; the last is the current state. */
        var visible = shown;
        var i;
        for (i = 0; i < entries.length; i += 1) {
          visible = entries[i].isIntersecting;
        }
        if (visible !== shown) {
          shown = visible;
          apply(false);
        }
      }).observe(wrapper);
    }

    watchSelection();
    watchVisibility();
    onSelect();
  }

  /* ---- REVEAL: the video fades in once it is really playing -----------------------------
   * The CSS half lives in sections/section-slideshow.liquid ("THE ENTRANCE"): with "Fade the
   * video in" on (data-ss-fade), an autoplaying video stays under a veil until its frame gets
   * `is-ready`. This decides when.
   *
   * WHY `playing` AND NOT `loadeddata`. loadeddata fires as soon as ONE frame is decoded, and
   * on a slow line that frame can then sit frozen for seconds while the rest buffers. That
   * frozen first frame is exactly what the fade exists to hide. `playing` means playback has
   * actually started, and requestVideoFrameCallback then waits for a frame to be PRESENTED, so
   * the fade never runs over an empty box. rAF stands in where rVFC is missing.
   *
   * A video that cannot play at all (PLAYFAIL: autoplay refused, e.g. iOS Low Power Mode) is
   * revealed anyway, on its poster: a slide that will never get its film should at least show
   * what it is. A video that does not autoplay is marked at once, since nothing hides it and its
   * wash should show.
   */
  function revealWhenPlaying(video) {
    var frame = video.closest('.ss-video-frame');
    if (!frame) {
      return;
    }
    var done = false;
    function reveal() {
      if (!done) {
        done = true;
        frame.classList.add('is-ready');
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener(PLAYFAIL, reveal);
      }
    }
    function onPlaying() {
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(reveal);
      } else {
        window.requestAnimationFrame(reveal);
      }
    }
    if (!video.parentNode.hasAttribute('data-ss-autoplay')) {
      reveal();
      return;
    }
    video.addEventListener('playing', onPlaying);
    video.addEventListener(PLAYFAIL, reveal);
    /* The first slide's video autoplays from the markup and may well be playing already. */
    if (!video.paused && video.readyState >= 2) {
      onPlaying();
    }
  }

  function setupReveal(wrapper) {
    if (wrapper.__dcSsReveal || !wrapper.hasAttribute('data-ss-fade')) {
      return;
    }
    wrapper.__dcSsReveal = true;
    var videos = wrapper.querySelectorAll('.ss-video-frame video');
    var i;
    for (i = 0; i < videos.length; i += 1) {
      revealWhenPlaying(videos[i]);
    }
    /* From here this file owns the reveal: the CSS fail-safe, which fades the video in after a
       few seconds when this file never ran, stands down. */
    wrapper.setAttribute('data-ss-armed', '');
  }

  /* ---- The blurred wash behind a contained video ---------------------------------------
   * Paints the foreground video's own frames into a 32px-wide canvas that CSS stretches over
   * the whole frame. Being the same frames, it cannot drift out of sync — there is nothing to
   * synchronise. See the long note above the .ss-video-frame__blur rules in
   * sections/section-slideshow.liquid for why this is a canvas and not a second <video>.
   */
  function setupFit(canvas) {
    if (canvas.__dcSsBlur) {
      return;
    }
    var frame = canvas.parentNode;
    var video = frame ? frame.querySelector('.ss-video-frame__media video') : null;
    if (!video) {
      return;
    }
    var ctx = canvas.getContext ? canvas.getContext('2d', { alpha: false }) : null;
    if (!ctx) {
      return;
    }
    canvas.__dcSsBlur = true;

    var sized = false;

    function paint() {
      /* videoWidth is 0 until the first frame is decodable, and drawImage throws
         InvalidStateError in that state — so this guard replaces a try/catch rather than
         merely avoiding wasted work. */
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        if (!sized) {
          /* Keep the footage's proportions in the bitmap (CSS then stretches it to the frame),
             so the wash reads as the same picture rather than a squashed one. */
          canvas.height = Math.max(1, Math.round((32 * video.videoHeight) / video.videoWidth));
          sized = true;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      schedule();
    }

    /* requestVideoFrameCallback is the right clock when it exists: it fires once per PRESENTED
       frame, so the wash updates exactly when the picture does and stops costing anything the
       moment the video pauses or its slide leaves the screen. rAF is the fallback, throttled —
       a 32px blurred wash has no need of 60fps. */
    var useVFC = typeof video.requestVideoFrameCallback === 'function';
    var last = 0;
    function schedule() {
      if (useVFC) {
        video.requestVideoFrameCallback(paint);
        return;
      }
      window.requestAnimationFrame(function (now) {
        if (now - last < 66) {
          schedule();
          return;
        }
        last = now;
        paint();
      });
    }
    schedule();
  }

  function boot() {
    var wrappers = document.querySelectorAll(SEL.wrapper);
    for (var i = 0; i < wrappers.length; i += 1) {
      /* REVEAL first, so its listeners are in place before PLAY calls play(). */
      setupReveal(wrappers[i]);
      setupPlay(wrappers[i]);
      setup(wrappers[i]);
    }
    var blurs = document.querySelectorAll('[data-ss-video-blur]');
    for (var j = 0; j < blurs.length; j += 1) {
      setupFit(blurs[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Flickity is initialised by theme.js after its own section load, and in the theme editor
     the whole section is replaced on every change — so re-run on both. `setup` is idempotent
     (it flags the wrapper), which is what makes calling it repeatedly free. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('theme:section:update', boot);
  window.setTimeout(boot, 1200);
})();
